import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { FLEET } from "@/data/fleet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = ["Giornaliero", "Weekend", "Settimanale", "Mensile", "Lungo termine"];
const LOCATIONS = ["Catania città", "Provincia di Catania", "Siracusa e provincia", "Messina e provincia", "Enna / Ragusa", "Altro"];

const Field = ({ label, children, dark }) => (
  <label className="block">
    <span className={`font-mono text-[10px] uppercase tracking-widest ${dark ? "text-white/50" : "text-ink/50"}`}>{label}</span>
    {children}
  </label>
);

export const QuoteForm = ({ defaultEquipment = "", dark = true }) => {
  const cls = dark ? "field-dark" : "field";
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    equipment: defaultEquipment, duration: "", location: "", start_date: "", transport: true, message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment || !form.duration || !form.location) {
      toast.error("Compila mezzo, durata e luogo di consegna");
      return;
    }
    if (form.name.trim().length < 2 || form.phone.trim().length < 6 || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Inserisci nome, telefono ed email validi");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/preventivi`, { ...form, company: form.company || null, start_date: form.start_date || null, message: form.message || null });
      toast.success("Richiesta inviata! Ti ricontattiamo entro poche ore.");
      setForm({ name: "", company: "", phone: "", email: "", equipment: defaultEquipment, duration: "", location: "", start_date: "", transport: true, message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invio non riuscito. Riprova o chiamaci.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="quote-form" noValidate>
      <Field label="Mezzo *" dark>
        <select required value={form.equipment} onChange={set("equipment")} className={`${cls} appearance-none`} data-testid="quote-form-vehicle-select">
          <option value="" className="text-ink">Seleziona un mezzo</option>
          {FLEET.map((v) => <option key={v.slug} value={v.name} className="text-ink">{v.name}</option>)}
          <option value="Non so, consigliatemi voi" className="text-ink">Non so, consigliatemi voi</option>
        </select>
      </Field>
      <Field label="Durata *" dark>
        <select required value={form.duration} onChange={set("duration")} className={`${cls} appearance-none`} data-testid="quote-form-duration-select">
          <option value="" className="text-ink">Seleziona la durata</option>
          {DURATIONS.map((d) => <option key={d} value={d} className="text-ink">{d}</option>)}
        </select>
      </Field>
      <Field label="Luogo di consegna *" dark>
        <select required value={form.location} onChange={set("location")} className={`${cls} appearance-none`} data-testid="quote-form-location-select">
          <option value="" className="text-ink">Seleziona la zona</option>
          {LOCATIONS.map((d) => <option key={d} value={d} className="text-ink">{d}</option>)}
        </select>
      </Field>
      <Field label="Data di inizio" dark>
        <input type="date" value={form.start_date} onChange={set("start_date")} className={cls} data-testid="quote-form-date-input" />
      </Field>
      <Field label="Nome e cognome *" dark>
        <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={cls} data-testid="quote-form-name-input" />
      </Field>
      <Field label="Azienda" dark>
        <input value={form.company} onChange={set("company")} placeholder="Impresa Edile Srl" className={cls} data-testid="quote-form-company-input" />
      </Field>
      <Field label="Telefono *" dark>
        <input required type="tel" minLength={6} value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={cls} data-testid="quote-form-phone-input" />
      </Field>
      <Field label="Email *" dark>
        <input required type="email" value={form.email} onChange={set("email")} placeholder="nome@azienda.it" className={cls} data-testid="quote-form-email-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Note sul lavoro" dark>
          <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Descrivi il lavoro: altezza da raggiungere, spazi disponibili, tipo di terreno…" className={`${cls} resize-none`} data-testid="quote-form-message-input" />
        </Field>
      </div>
      <label className="flex items-center gap-3 text-sm text-white/70 sm:col-span-2">
        <input type="checkbox" checked={form.transport} onChange={set("transport")} className="h-4 w-4 accent-blue-500" data-testid="quote-form-transport-checkbox" />
        Includi trasporto in cantiere (consegna e ritiro)
      </label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid="quote-form-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Richiedi preventivo <ArrowRight className="h-4 w-4" /></>}
        </button>
        <p className="mt-3 text-xs text-white/40">Ti rispondiamo in giornata negli orari di apertura. Nessun impegno.</p>
      </div>
    </form>
  );
};
